import { api } from './client'
import type { StockNoteRollbackRefs } from './stock-note-ref'

// ============================================================================
// Lô phân loại — pattern PNK + PXK với items M-N (Phase 3-lots-redesign)
// Status sống trên phiếu: pending|approved|rejected|cancelled
// ============================================================================

export type NoteStatus = 'pending' | 'approved' | 'rejected' | 'cancelled'

export type PackagingLotInReason =
    | 'from_purchase'
    | 'adjustment'
    | 'return_from_packaging_process'
    | 'return_from_order'
    | 'from_order_cancel'
export type PackagingLotOutReason =
    'for_order' | 'discard' | 'adjustment' | 'for_packaging_process'

export interface PackagingLot {
    id: string
    code: string
    packaging_material_id: string
    material_name?: string
    material_sku?: string
    material_unit?: string
    warehouse_id: string
    warehouse_name?: string
    position_id?: string | null
    position_code?: string | null
    position_name?: string | null
    supplier_name?: string | null
    quantity_available: number
    notes?: string | null
    created_by_id?: string | null
    created_by_name?: string
    created_at: string
    updated_at: string
}

export interface PackagingLotStockInNoteItem {
    id: string
    note_id: string
    packaging_lot_id: string
    packaging_lot_code?: string
    material_name?: string
    position_code?: string | null
    quantity: number
    created_at: string
}

export interface PackagingLotStockInNote extends StockNoteRollbackRefs {
    id: string
    code: string
    status: NoteStatus
    reason: PackagingLotInReason
    notes?: string | null
    created_by_id?: string | null
    created_by_name?: string
    approved_by_id?: string | null
    approved_by_name?: string
    approved_at?: string | null
    rejected_reason?: string | null
    cancelled_at?: string | null
    order_id?: string | null
    order_code?: string
    stocktake_session_id?: string | null
    stocktake_session_code?: string
    created_at: string
    updated_at: string
    items_count?: number
    items?: PackagingLotStockInNoteItem[]
}

export type PackagingLotStockOutNoteItem = PackagingLotStockInNoteItem

export interface PackagingLotStockOutNote extends Omit<
    PackagingLotStockInNote,
    'reason' | 'items'
> {
    reason: PackagingLotOutReason
    items?: PackagingLotStockOutNoteItem[]
}

// ============================================================================
// Phiếu lấy hàng (withdrawal note) — phiếu độc lập, không đụng tồn kho.
// Vòng đời: open|closed|cancelled. Không có bước duyệt.
// ============================================================================

export type WithdrawalNoteStatus = 'open' | 'closed' | 'cancelled'

export interface PackagingLotWithdrawalNoteItem {
    id: string
    note_id: string
    packaging_lot_id: string
    packaging_lot_code?: string
    material_name?: string
    position_code?: string | null
    quantity: number
    created_at: string
}

export interface PackagingLotWithdrawalNote {
    id: string
    code: string
    status: WithdrawalNoteStatus
    notes?: string | null
    created_by_id?: string | null
    created_by_name?: string
    created_at: string
    updated_at: string
    items_count?: number
    items?: PackagingLotWithdrawalNoteItem[]
}

export interface CreateWithdrawalNoteItemInput {
    packaging_lot_id: string
    quantity: number
}

export interface CreateWithdrawalNoteRequest {
    notes?: string
    items: CreateWithdrawalNoteItemInput[]
}

export interface ListWithdrawalNotesParams {
    status?: WithdrawalNoteStatus
    search?: string
    date_from?: string
    date_to?: string
    page?: number
    per_page?: number
}

export interface PackagingLotHistoryEntry {
    note_id: string
    note_code: string
    note_type: 'in' | 'out'
    status: NoteStatus
    reason: string
    quantity: number
    notes?: string | null
    created_at: string
    approved_at?: string | null
    created_by_name?: string
    /** Chỉ có với PXK reason=for_order — mã đơn hàng + mã đơn nội bộ của đơn. */
    order_code?: string | null
    internal_order_code?: string | null
}

// ============================================================================
// Request shapes
// ============================================================================

export type CreateInNoteItemInput =
    | { packaging_lot_id: string; quantity: number }
    | {
          new_lot_packaging_material_id: string
          new_lot_code?: string
          quantity: number
      }

export interface CreateInNoteRequest {
    reason: PackagingLotInReason
    notes?: string
    items: CreateInNoteItemInput[]
}

/** Sửa PNK vật tư đang pending. Reason bất biến. */
export interface UpdateInNoteRequest {
    reason: PackagingLotInReason
    notes?: string
    items: CreateInNoteItemInput[]
}

export interface CreateOutNoteItemInput {
    packaging_lot_id: string
    quantity: number
}

export interface CreateOutNoteRequest {
    reason: PackagingLotOutReason
    notes?: string
    items: CreateOutNoteItemInput[]
    /** Tuỳ chọn ngay cả với reason=`for_order`: xuất gộp cuối ngày theo sổ kiểm
     *  kê túi không móc vào đơn nào. Bỏ qua ở reason khác. */
    order_id?: string
    /** #M-16: mẻ sổ kiểm kê túi đã sinh ra phiếu này — server nối ngược về sổ. */
    bag_count_entry_id?: string
    /** #M-16: mốc sửa của mẻ lúc màn phiếu lấy số về; lệch nghĩa là mẻ vừa bị
     *  sửa và số trên phiếu đã lỗi thời, server trả 400. */
    bag_count_updated_at?: string
}

/** Sửa PXK vật tư đang pending. Reason bất biến (order_id giữ nguyên). */
export interface UpdateOutNoteRequest {
    reason: PackagingLotOutReason
    notes?: string
    items: CreateOutNoteItemInput[]
}

export interface ListLotsParams {
    material_id?: string
    warehouse_id?: string
    position_id?: string
    search?: string
    only_available?: '1' | undefined
    /** '1' → ẩn lô ảo 'STOCK' (qty-mode) khỏi danh sách. Picker xuất bỏ trống để
     *  vẫn chọn xuất qty-mode. */
    exclude_virtual?: '1' | undefined
    page?: number
    per_page?: number
}

export interface ListNotesParams {
    status?: NoteStatus
    reason?: string
    date_from?: string
    date_to?: string
    page?: number
    per_page?: number
}

// ============================================================================
// API endpoints
// ============================================================================

const BASE = '/packaging-lots'

export const packagingLotApi = {
    // Lots
    listLots: (params?: ListLotsParams) =>
        api.get<PackagingLot[]>(BASE, { params }),
    // Xuất Excel danh sách lô (BE tự quét toàn bộ lô còn khả dụng, bỏ qua page).
    exportExcel: (params?: ListLotsParams) =>
        api.getBlob(`${BASE}/export`, { params }),
    getLot: (id: string) => api.get<PackagingLot>(`${BASE}/${id}`),
    getLotHistory: (
        id: string,
        params?: { page?: number; per_page?: number },
    ) =>
        api.get<PackagingLotHistoryEntry[]>(`${BASE}/${id}/history`, {
            params,
        }),

    // Stock-in notes
    listInNotes: (params?: ListNotesParams) =>
        api.get<PackagingLotStockInNote[]>(`${BASE}/stock-in-notes`, {
            params,
        }),
    getInNote: (id: string) =>
        api.get<PackagingLotStockInNote>(`${BASE}/stock-in-notes/${id}`),
    createInNote: (req: CreateInNoteRequest) =>
        api.post<PackagingLotStockInNote>(`${BASE}/stock-in-notes`, req),
    updateInNote: (id: string, req: UpdateInNoteRequest) =>
        api.put<PackagingLotStockInNote>(`${BASE}/stock-in-notes/${id}`, req),
    approveInNote: (id: string) =>
        api.post<void>(`${BASE}/stock-in-notes/${id}/approve`),
    rejectInNote: (id: string, reason: string) =>
        api.post<void>(`${BASE}/stock-in-notes/${id}/reject`, { reason }),
    cancelInNote: (id: string) =>
        api.post<void>(`${BASE}/stock-in-notes/${id}/cancel`),
    /** Rollback PNK đã duyệt → BE sinh PXK điều chỉnh, trả phiếu vừa sinh. */
    rollbackInNote: (id: string) =>
        api.post<PackagingLotStockOutNote>(
            `${BASE}/stock-in-notes/${id}/rollback`,
        ),

    // Stock-out notes
    listOutNotes: (params?: ListNotesParams) =>
        api.get<PackagingLotStockOutNote[]>(`${BASE}/stock-out-notes`, {
            params,
        }),
    /**
     * PXK vật tư đã duyệt, lý do "xuất sang đóng gói" — nguồn (tuỳ chọn) cho
     * picker khi tạo PNK sản phẩm. BE giới hạn 100 dòng; dùng search để thu hẹp.
     */
    listOutNotesForPackagingProcess: (search?: string) =>
        api.get<PackagingLotStockOutNote[]>(
            `${BASE}/stock-out-notes/for-packaging-process`,
            { params: search ? { search } : undefined },
        ),
    getOutNote: (id: string) =>
        api.get<PackagingLotStockOutNote>(`${BASE}/stock-out-notes/${id}`),
    createOutNote: (req: CreateOutNoteRequest) =>
        api.post<PackagingLotStockOutNote>(`${BASE}/stock-out-notes`, req),
    updateOutNote: (id: string, req: UpdateOutNoteRequest) =>
        api.put<PackagingLotStockOutNote>(`${BASE}/stock-out-notes/${id}`, req),
    approveOutNote: (id: string) =>
        api.post<void>(`${BASE}/stock-out-notes/${id}/approve`),
    rejectOutNote: (id: string, reason: string) =>
        api.post<void>(`${BASE}/stock-out-notes/${id}/reject`, { reason }),
    cancelOutNote: (id: string) =>
        api.post<void>(`${BASE}/stock-out-notes/${id}/cancel`),
    /** Rollback PXK đã duyệt → BE sinh PNK điều chỉnh, trả phiếu vừa sinh. */
    rollbackOutNote: (id: string) =>
        api.post<PackagingLotStockInNote>(
            `${BASE}/stock-out-notes/${id}/rollback`,
        ),

    // Phiếu lấy hàng (withdrawal notes)
    listWithdrawalNotes: (params?: ListWithdrawalNotesParams) =>
        api.get<PackagingLotWithdrawalNote[]>(`${BASE}/withdrawal-notes`, {
            params,
        }),
    getWithdrawalNote: (id: string) =>
        api.get<PackagingLotWithdrawalNote>(`${BASE}/withdrawal-notes/${id}`),
    createWithdrawalNote: (req: CreateWithdrawalNoteRequest) =>
        api.post<PackagingLotWithdrawalNote>(`${BASE}/withdrawal-notes`, req),
    cancelWithdrawalNote: (id: string) =>
        api.post<void>(`${BASE}/withdrawal-notes/${id}/cancel`),
}
